import React, { useState, useRef, useEffect } from 'react';
import { getOrderId, isMobile } from '@/biz/tool';
import toast from 'react-hot-toast';
import WebModal from './Modal';
import BottomDrawer from './BottomDrawer';
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import appConfig from '@/utils/config';
import { NationsLab } from '@/utils/locations';
import { useLoginStore } from '@/hooks/useLoginStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { getUserLang } from '@/i18n/utils';
import { TriangleExclamationIcon, WarnRedIcon } from '@/components/common/icon';
import getHoursDiffFromNow from '@/utils/getHoursDiffFromNow';

import styles from './ModalAddress.module.less';

// 获取当前语言并映射到 Stripe 支持的语言代码
const getCurrentLocale = () => {
  const lang = getUserLang().detectedValue;
  const localeMap = {
    'en-US': 'en',
    'pt-BR': 'pt-BR',
    'ja-JP': 'ja'
  } as const;
  return localeMap[lang as keyof typeof localeMap] || 'en';
};

const stripePromise = loadStripe(appConfig['stripe_public_key'], {
  locale: getCurrentLocale()
});

// RFC 完整验证函数
const validateRFC = (value: string): boolean => {
  // 1. 长度验证
  if (value.length !== 13) {
    return false;
  }

  // 2. 前4位：仅字母（A-Z + 带重音的西班牙语大写字母）
  const first4 = value.substring(0, 4);
  // A-Z (U+0041-U+005A) + Latin-1 Supplement (U+00C0-U+00FF) 包含带重音的字母
  if (!/^[A-Z\u00C0-\u00FF]{4}$/.test(first4)) {
    return false;
  }

  // 3. 中间6位：日期 YYMMDD
  const dateStr = value.substring(4, 10);
  if (!/^\d{6}$/.test(dateStr)) {
    return false;
  }

  // 解析日期
  const yy = parseInt(dateStr.substring(0, 2));
  const mm = parseInt(dateStr.substring(2, 4));
  const dd = parseInt(dateStr.substring(4, 6));

  // 验证年月日范围
  if (mm < 1 || mm > 12) {
    return false;
  }

  // 年份转换和验证
  // RFC 年份解读：
  // - YY 50-99 -> 1950-1999
  // - YY 00-当前年份后两位 -> 2000-当前年
  const currentYear = new Date().getFullYear();
  const currentYearLastTwo = currentYear % 100;
  let fullYear;

  if (yy >= 50 && yy <= 99) {
    fullYear = 1900 + yy; // 1950-1999
  } else if (yy >= 0 && yy <= currentYearLastTwo) {
    fullYear = 2000 + yy; // 2000-当前年
  } else {
    return false; // 年份超出范围
  }

  // RFC 最早日期：1950-01-01
  if (fullYear < 1950) {
    return false;
  }

  // 验证日期是否有效
  const daysInMonth = new Date(fullYear, mm, 0).getDate();
  if (dd < 1 || dd > daysInMonth) {
    return false;
  }

  // 验证日期不能超过今天
  const date = new Date(fullYear, mm - 1, dd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) {
    return false;
  }

  // 4. 后3位：字母或数字
  const last3 = value.substring(10, 13);
  if (!/^[A-Z0-9]{3}$/.test(last3)) {
    return false;
  }

  return true;
};

interface Props {}

const ModalAddress: React.FC<Props> = ({}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { isShowAddressModal, setShowAddressModal } = useWebModalStore();
  const { setNowSuccessNode, setShowModalBonusNext, setShowModalNodeSuccess } =
    useWebModalStore();
  const [complete, setComplete] = useState(false);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [address, setAddress] = useState(null);
  const orderId = getOrderId();

  const handleSubmit = async () => {
    try {
      await axiosInstance.post(`/order/${orderId}/address`, {
        addressInfo: address
      });
      reQueryDetailData({ orderId });
      setShowAddressModal(false);
      // 此处区分有无bonus，弹不同提示弹窗
      // 填写地址逾期时，要等提交成功后，才会清零奖金，所以特殊处理下，判断有没有逾期
      // 但是这个又有一种情况是，可以申诉，申诉成功后，可以继续获得奖金 所以新增判断当前阶段是否申诉成功过
      const delayTime = getHoursDiffFromNow(
        detailData?.order?.creatorNextOperateDate
      );
      setNowSuccessNode(detailData.order?.displayStatus);
      if (
        (delayTime > 0 || detailData.order?.isCurrentStageHasApprovedAppeal) &&
        detailData?.order?.interestedPriceFields?.[0]?.bonusAmount > 0
      ) {
        setShowModalBonusNext(true);
      } else {
        setShowModalNodeSuccess(true);
      }
    } catch (error) {
      toast.error(
        t('modal.address.submit.error', { defaultValue: 'Submission failed.' })
      );
    }
  };

  // locations
  const handleAddressChange = (event: any) => {
    const { complete, value, rfcValue } = event;
    const locations: any[] = detailData?.campaign?.locations || [];

    const { address, name, phone } = value;
    setAddress({
      ...address,
      name,
      phone,
      rfcValue: address?.country === 'MX' ? rfcValue : ''
    });
    setStripeComplete(complete);
    const isInclude = locations?.some((item: string[]) =>
      item?.includes(value?.address?.country)
    );
    if (!isInclude) {
      toast.error(
        t('modal.address.location.error', {
          defaultValue:
            "We're sorry that your location is not meeting the brand's requirements. To begin this collaboration, please ensure that you are located in one of the following countries: {{countries}}.",
          countries: locations
            ?.map((item) => NationsLab?.[item?.[1]])
            .join(', ')
        })
      );
      setComplete(false);
      return;
    }

    if (address?.country === 'MX' && !validateRFC(rfcValue)) {
      setComplete(false);
      return;
    }
    setComplete(complete);
  };

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          title={t('modal.address.title', {
            defaultValue: 'Enter your address details'
          })}
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          submitDisabled={!complete}
          okText={t('modal.address.continue', { defaultValue: 'Continue' })}
        >
          <ModalContent
            handleAddressChange={handleAddressChange}
            addressInfo={address}
            complete={stripeComplete}
          />
        </BottomDrawer>
      ) : (
        <WebModal
          title={t('modal.address.title', {
            defaultValue: 'Enter your address details'
          })}
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          submitDisabled={!complete}
          okText={t('modal.address.confirm', { defaultValue: 'Confirm' })}
        >
          <ModalContent
            handleAddressChange={handleAddressChange}
            addressInfo={address}
            complete={stripeComplete}
          />
        </WebModal>
      )}
    </>
  );
};

const ModalContent: React.FC<{
  handleAddressChange: any;
  addressInfo: any;
  complete: boolean;
}> = ({ handleAddressChange, addressInfo, complete }) => {
  const { t } = useTranslation();
  const { userAddressInfo } = useLoginStore();

  const { name, phone, ...address } = userAddressInfo || {};

  const { detailData } = useDetailDataStore();
  const { setEducationModal } = useWebModalStore();
  const [rfcValue, setRfcValue] = useState(address?.rfcValue || '');
  const [showRfcError, setShowRfcError] = useState(false);
  const rfcInputRef = useRef<HTMLInputElement>(null);

  const openOverduePolicy = (e: React.MouseEvent) => {
    setEducationModal(true, { order: detailData?.order });
  };

  // 值变化校验
  const handleRfcChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const oldCursorPosition = input.selectionStart || 0;

    // 移除所有空格，保持光标位置
    const newValue = input.value.toUpperCase().replace(/\s/g, '');

    // 计算移除的空格数量（光标之前的空格）
    const spacesBeforeCursor = input.value
      .substring(0, oldCursorPosition)
      .replace(/\S/g, '').length;
    const newCursorPosition = Math.max(
      0,
      oldCursorPosition - spacesBeforeCursor
    );

    setRfcValue(newValue);
    const info = addressInfo ? addressInfo : userAddressInfo;
    const { name, phone, ...address } = info || {};
    handleAddressChange({
      ...{ value: { name, address, phone } },
      rfcValue: newValue,
      complete
    });
    if (newValue && !validateRFC(newValue)) {
      setShowRfcError(true);
    } else {
      setShowRfcError(false);
    }

    // 恢复光标位置
    setTimeout(() => {
      if (rfcInputRef.current) {
        rfcInputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);
  };

  return (
    <div className={isMobile() ? styles.mobileContent : styles.content}>
      <div className={styles.desc}>
        <span>
          {t('modal.address.description', {
            defaultValue:
              'Please enter your address to receive the product from the brand. Your address information is secure and protected, and it will only be used for the purpose of shipping the product to you.'
          })}
        </span>
        <div className="w-full mt-4 px-4 py-3 bg-[#FFFBEB] rounded-lg inline-flex justify-start items-start gap-8">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
            <div className="flex justify-start items-top gap-2">
              <div>
                <TriangleExclamationIcon className="w-5 h-5" />
              </div>
              <div className="self-stretch inline-flex justify-start items-center">
                <div className="flex-1 justify-center">
                  <span className="text-[#57534D] text-sm font-normal leading-snug">
                    {t('modal.address.address_submission_reminder', {
                      defaultValue:
                        'Once the address has been submitted, the product is non-returnable. Please note that failing to complete the deal after receiving your items will negatively impact your creator rating and make you liable for reimbursing the full product cost plus any shipping fees.'
                    })}
                  </span>
                  <span
                    className="text-[#2B7FFF] text-sm font-normal leading-snug cursor-pointer"
                    onClick={openOverduePolicy}
                  >
                    {' '}
                    {t('todos_card.time_is_up_jump', {
                      defaultValue: 'View timeline policy'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.stripeElements}>
        <Elements
          stripe={stripePromise}
          options={{
            appearance: {
              variables: {
                fontFamily: "'Inter'",
                fontWeightNormal: '600'
              },
              rules: {
                '.Label': {
                  fontFamily: "'Inter'",
                  color: '#57534D',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  opacity: '1'
                }
              }
            }
          }}
        >
          <AddressElement
            options={{
              mode: 'billing',
              fields: {
                phone: 'always'
              },
              validation: {
                phone: {
                  required: 'always'
                }
              },
              defaultValues: {
                name,
                address,
                phone
              }
            }}
            onChange={(event) => {
              handleAddressChange({
                ...event,
                rfcValue: rfcValue || address?.rfcValue || ''
              });
            }}
          />
        </Elements>
      </div>
      {addressInfo?.country === 'MX' && (
        <div className="flex flex-col gap-2 mt-4 w-full">
          <div className="justify-start">
            <span className="text-[#57534D] text-[12px] font-['Inter'] leading-5 font-[500]">
              {t('modal.address.rfc.label', {
                defaultValue: 'Tax Identification Number (RFC)'
              })}
            </span>
            <span className="text-[#DC2626] text-sm font-['Inter'] leading-5">
              *
            </span>
          </div>
          <div className="w-full">
            <div
              className={`h-10 max-h-10 min-h-10 px-3 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] ${
                showRfcError ? 'outline-[#DC2626]' : 'outline-zinc-200'
              } flex justify-end items-center gap-16`}
            >
              <input
                ref={rfcInputRef}
                className="flex-1 w-full text-start text-[#292524] text-sm font-medium leading-5 bg-transparent border-none outline-none placeholder:text-[#A6A09B]"
                value={rfcValue}
                onChange={handleRfcChange}
                placeholder={t('modal.address.rfc.placeholder', {
                  defaultValue: 'e.g. GODE561231GR8'
                })}
                type="text"
              />
            </div>
            {showRfcError && (
              <div className="px-4 py-0.5 bg-[#FEE2E2] rounded flex justify-center items-start gap-1 mt-[10px]">
                <WarnRedIcon className="w-3 h-3 mt-1" />
                <div className="flex-1 justify-start text-[#DC2626] text-xs font-normal font-['Inter'] leading-5">
                  {t('modal.address.rfc.error_message', {
                    defaultValue:
                      'Invalid RFC format. Your RFC must contain 13 characters: 4 letters, 6 digits for your birth date (YYMMDD), and 3 alphanumeric characters.'
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalAddress;
